import type { paths } from '@st/api/paths'
import type { ResponseBody } from '@st/openapi-tools'
import { useVipClubDeps } from '../../useDeps'

type AuthLoyaltyData = ResponseBody<
  paths,
  '/carrot/bonus-programs/loyalty/personal/find',
  'post'
>[number]

type NonAuthLoyaltyData = ResponseBody<
  paths,
  '/carrot/bonus-programs/loyalty/find',
  'post'
>[number]

export function isAuthLoyaltyData(
  data: AuthLoyaltyData | NonAuthLoyaltyData,
): data is AuthLoyaltyData {
  return (data as AuthLoyaltyData).data.progressCurrency !== undefined
}

export const useVipClubStore = defineStore('vipClub', () => {
  const { isAuthenticated } = useVipClubDeps()

  const {
    data: cashbackData,
    refresh: refreshCashback,
    status: cashbackStatus,
  } = useStFetch('/carrot/bonus-programs/cashback/personal/find', {
    method: 'post',
    immediate: false,
  })

  const {
    data: reloadData,
    refresh: refreshReload,
    status: reloadStatus,
  } = useStFetch('/carrot/bonus-programs/reload/personal/find', {
    method: 'post',
    immediate: false,
  })

  const {
    data: loyaltyPersonalData,
    refresh: refreshLoyalty,
    status: loyaltyStatus,
  } = useStFetch('/carrot/bonus-programs/loyalty/personal/find', {
    method: 'post',
    immediate: false,
  })

  const { data: loyaltyUnauthorizedData, refresh: refreshLoyaltyUnauthorized } =
    useStFetch('/carrot/bonus-programs/loyalty/find', {
      method: 'post',
      immediate: false,
    })

  const loading = computed(
    () =>
      cashbackStatus.value === 'pending' ||
      reloadStatus.value === 'pending' ||
      loyaltyStatus.value === 'pending',
  )

  const error = computed(
    () =>
      cashbackStatus.value === 'error' ||
      reloadStatus.value === 'error' ||
      loyaltyStatus.value === 'error',
  )

  function refreshBonuses() {
    if (isAuthenticated.value) {
      refreshCashback()
      refreshReload()
      refreshLoyalty()
    } else {
      refreshLoyaltyUnauthorized()
    }
  }

  watch(
    () => isAuthenticated.value,
    () => {
      refreshBonuses()
    },
    { immediate: true },
  )

  const loyaltyData = computed<AuthLoyaltyData[] | NonAuthLoyaltyData[] | null>(
    () =>
      isAuthenticated.value
        ? loyaltyPersonalData.value
        : loyaltyUnauthorizedData.value,
  )

  return {
    cashbackData,
    reloadData,
    loyaltyData,
    loyaltyUnauthorizedData,
    loading,
    error,
  }
})
